/**
 * AuthProvider - Supabase Authentication Context for Web
 *
 * Story: 0-6-frontend-auth-integration
 *
 * This provider manages authentication state using Supabase.
 * It replaces the deprecated tokenManager-based auth with Supabase sessions.
 *
 * Features:
 * - Auth state listener for session changes
 * - Session persistence via localStorage (Supabase default)
 * - Auto-refresh of access tokens (handled by Supabase SDK)
 * - User and session state exposed via useAuth() hook
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { supabase, onAuthStateChange, getSession, runSupabaseStorageProbe } from '@/lib/supabase';
import type { User, Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { clearPersonalMemberCardCache } from '@/utils/personal-member-card-cache';
import { AuthContext } from './auth-context';
import { captureStructuredLog, OBSERVABILITY_EVENTS } from '@/lib/observability';

// ============================================================================
// PROVIDER
// ============================================================================

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const lastIdentity = useRef<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from stored session
  useEffect(() => {
    let mounted = true;

    async function initializeAuth() {
      try {
        runSupabaseStorageProbe();
        const sessionResult = await getSession();
        const storedSession = sessionResult?.data?.session ?? null;

        if (mounted) {
          if (lastIdentity.current !== (storedSession?.user?.id ?? null)) clearPersonalMemberCardCache(queryClient);
          lastIdentity.current = storedSession?.user?.id ?? null;
          setSession(storedSession);
          setUser(storedSession?.user ?? null);
          setLoading(false);
        }
      } catch (error) {
        console.error('[AuthProvider] Error loading initial session:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    }

    void initializeAuth();

    return () => {
      mounted = false;
    };
  }, [queryClient]);

  // Subscribe to auth state changes
  useEffect(() => {
    const authChange = onAuthStateChange((event, newSession) => {
      console.log('[AuthProvider] Auth state changed:', event);

      const userId = newSession?.user?.id ?? null;
      if (event === 'SIGNED_OUT' || userId !== lastIdentity.current) clearPersonalMemberCardCache(queryClient);
      lastIdentity.current = userId;
      captureStructuredLog(OBSERVABILITY_EVENTS.LOG_AUTH_STATE_CHANGED, {
        auth_event: event,
        user_id: userId,
      }, {
        dedupeKey: `auth_state:${event}:${userId ?? 'anonymous'}`,
        level: 'info',
      });

      setSession(newSession);
      setUser(newSession?.user ?? null);

      // Handle specific events
      switch (event) {
        case 'INITIAL_SESSION':
          console.log('[AuthProvider] Initial session loaded');
          break;
        case 'SIGNED_IN':
          console.log('[AuthProvider] User signed in:', newSession?.user?.id);
          break;
        case 'SIGNED_OUT':
          console.log('[AuthProvider] User signed out');
          break;
        case 'TOKEN_REFRESHED':
          console.log('[AuthProvider] Token refreshed');
          captureStructuredLog(OBSERVABILITY_EVENTS.LOG_AUTH_TOKEN_REFRESHED, {
            auth_event: event,
            user_id: userId,
          }, {
            dedupeKey: `token_refreshed:${userId ?? 'anonymous'}`,
            level: 'info',
          });
          break;
        case 'USER_UPDATED':
          console.log('[AuthProvider] User updated');
          break;
        case 'PASSWORD_RECOVERY':
          console.log('[AuthProvider] Password recovery flow active');
          break;
        case 'MFA_CHALLENGE_VERIFIED':
          console.log('[AuthProvider] MFA challenge verified');
          break;
      }
    });

    return () => {
      authChange?.data?.subscription?.unsubscribe();
    };
  }, [queryClient]);

  /**
   * Sign out the current user
   */
  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('[AuthProvider] Sign out error:', error);
        throw error;
      }
      // State will be updated by onAuthStateChange listener
    } catch (error) {
      console.error('[AuthProvider] Sign out failed:', error);
      throw error;
    }
  }, []);

  /**
   * Get current access token for API calls
   * Returns null if not authenticated or token unavailable
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      // Always get fresh session to ensure token is valid
      const result = await getSession();
      return result?.data?.session?.access_token ?? null;
    } catch (error) {
      console.error('[AuthProvider] Error getting access token:', error);
      return null;
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    session,
    loading,
    isAuthenticated: !!session,
    signOut,
    getAccessToken,
  }), [user, session, loading, signOut, getAccessToken]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
